import { UseFormSetValue } from 'react-hook-form';
import { DecomPolicyFormValues } from '../../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../../api/vault-api.ts';
import { ReactNode, useMemo } from 'react';
import SharedDataGridClient from '../../../../../../shared/shared-data-grid-client.tsx';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { clearFieldError } from '../decom-action.tsx';
import { ActionErrorEntry } from '../../decom-policy-utils.tsx';
import SharedButton from '../../../../../../shared/buttons/shared-button.tsx';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined';
import Search from '@mui/icons-material/Search';

interface VaultMatterTableProps {
  matters: VaultMatterModel[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  matterId: string;
  day: number;
  idx: number;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  setIsCreateMatterOpen: (open: boolean) => void;
  hasUnfilteredData: boolean;
  hasError?: boolean;
  actionType: string;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  disabled?: boolean;
}

export default function VaultMatterTable({
  matters,
  isLoading,
  isError,
  refetch,
  matterId,
  day,
  idx,
  setValue,
  setIsCreateMatterOpen,
  hasUnfilteredData,
  hasError,
  actionType,
  setActionTypesWithErrors,
  disabled,
}: VaultMatterTableProps) {
  const theme = useTheme();

  const columns: GridColDef<VaultMatterModel>[] = useMemo(
    () => [
      {
        field: 'select',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params: GridRenderCellParams<VaultMatterModel>) => (
          <Radio
            checked={matterId === params.row.matterId}
            disabled={disabled}
            onChange={() => {
              setValue(
                `actions.${day}.actions.${idx}.matterId`,
                params.row.matterId,
                {
                  shouldDirty: true,
                  shouldValidate: true,
                }
              );
              clearFieldError(actionType, 'matterId', setActionTypesWithErrors);
            }}
            value={params.row.matterId}
            sx={{
              p: 0.5,
            }}
          />
        ),
      },
      {
        field: 'name',
        headerName: 'Matter Name',
        flex: 1,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => row.name,
      },
    ],
    [
      matterId,
      day,
      idx,
      setValue,
      actionType,
      setActionTypesWithErrors,
      disabled,
    ]
  );

  const renderMessageAlert = (children: ReactNode) => {
    return (
      <Box sx={{ p: 0, textAlign: 'center' }}>
        <Box
          sx={{
            bgcolor: theme.palette.semantic.surface.primary3,
            border: '1px solid',
            borderColor: theme.palette.semantic.outline.primary,
            borderRadius: 1,
            p: 5,
            textAlign: 'center',
            mt: 2,
          }}
        >
          {children}
        </Box>
      </Box>
    );
  };

  if (isError) {
    return renderMessageAlert(
      <Stack alignItems="center" spacing={1}>
        <Typography color="error">
          Failed to load matters. Please try again.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </Stack>
    );
  }

  if (!isLoading && matters.length === 0) {
    return renderMessageAlert(
      <Stack alignItems="center" spacing={1.5}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.semantic.surface.bg1,
            borderRadius: '50%',
            width: 64,
            height: 64,
          }}
        >
          {!hasUnfilteredData ? (
            <CreateNewFolderOutlined
              sx={{ fontSize: 28, color: theme.palette.semantic.icon.primary }}
            />
          ) : (
            <Search
              sx={{ fontSize: 28, color: theme.palette.semantic.icon.primary }}
            />
          )}
        </Box>
        <Stack alignItems="center" spacing={0.25}>
          {hasUnfilteredData && (
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.semantic.text.title2,
                fontWeight: 600,
              }}
            >
              No matters found.
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              color: !hasUnfilteredData
                ? theme.palette.semantic.text.title2
                : theme.palette.semantic.text.body,
            }}
          >
            {!hasUnfilteredData
              ? "There aren't any matters created in this organization."
              : 'Try updating your search or filters.'}
          </Typography>
        </Stack>
        {hasUnfilteredData && (
          <SharedButton
            variant="primary"
            onClick={() => setIsCreateMatterOpen(true)}
          >
            Create Matter
          </SharedButton>
        )}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 350,
        border: hasError ? '1px solid' : undefined,
        borderColor: hasError ? theme.palette.error.main : undefined,
        borderRadius: hasError ? 1 : 0,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        mt: 2,
      }}
    >
      <SharedDataGridClient
        rows={matters}
        columns={columns}
        getRowId={(row) => row.matterId}
        loading={isLoading}
        showToolbar={false}
        pagination={true}
        pageSizeOptions={[25, 50, 100]}
        defaultGridState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
              page: 0,
            },
          },
        }}
        rowHeight={52}
        hideFooterRowCount={true}
        sx={{
          height: '100%',
          boxShadow: 'none',
          border: 'none',
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-cell': {
            outline: 'none !important',
            borderRight: 'none !important',
          },
          '& .MuiDataGrid-cellFocus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeaderFocus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none !important',
          },
        }}
      />
    </Box>
  );
}
